import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, MapPin, ArrowLeft } from "lucide-react";

export default function CrewDetail() {
    const { slug } = useParams();
    const { user } = useAuth();
    const [crew, setCrew] = useState(null);
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get(`/crews/${slug}`);
            setCrew(data);
        } catch (_e) {
            setCrew(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, [slug]); // eslint-disable-line

    const toggleJoin = async () => {
        if (!user || joining) return;
        setJoining(true);
        try {
            const { data } = await api.post(`/crews/${slug}/join`);
            setCrew((prev) => ({
                ...prev,
                members: data.joined
                    ? [...(prev.members || []), user.id]
                    : (prev.members || []).filter((id) => id !== user.id),
                members_count: data.members_count,
            }));
        } catch (_e) {
            /* ignore */
        } finally {
            setJoining(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[var(--crewz-bg)]">
                <Navbar />
                <div className="max-w-5xl mx-auto px-6 py-20 text-zinc-500 font-body">
                    Loading crew…
                </div>
            </div>
        );
    }

    if (!crew) {
        return (
            <div className="min-h-screen bg-[var(--crewz-bg)]">
                <Navbar />
                <div className="max-w-5xl mx-auto px-6 py-20 text-zinc-300 font-body">
                    Crew not found.{" "}
                    <Link to="/crews" className="text-red-500 hover:text-red-400">
                        Back to crews
                    </Link>
                </div>
            </div>
        );
    }

    const isMember = user && (crew.members || []).includes(user.id);

    return (
        <div className="min-h-screen bg-[var(--crewz-bg)]" data-testid={`crew-detail-${slug}`}>
            <Navbar />
            <div className="relative h-64 md:h-80 overflow-hidden border-b border-white/5">
                {crew.cover_url && (
                    <img
                        src={crew.cover_url}
                        alt={crew.name}
                        className="w-full h-full object-cover opacity-60"
                    />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--crewz-bg)] to-transparent" />
                <div className="absolute bottom-6 left-6 md:left-12 right-6">
                    <Link
                        to="/crews"
                        className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white font-body mb-3"
                    >
                        <ArrowLeft size={12} /> All crews
                    </Link>
                    <p className="overline">
                        {crew.focus === "cars" ? "Cars" : crew.focus === "bikes" ? "Bikes" : "Mixed"}
                    </p>
                    <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-white">
                        {crew.name}
                    </h1>
                </div>
            </div>

            <main className="max-w-5xl mx-auto px-6 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2">
                    <p className="overline">About</p>
                    <p className="font-body text-zinc-300 leading-relaxed mt-3 whitespace-pre-wrap">
                        {crew.description || "No description yet."}
                    </p>

                    <div className="flex flex-wrap gap-4 mt-6 text-sm text-zinc-400 font-body">
                        <span className="inline-flex items-center gap-1.5">
                            <Users size={14} /> {crew.members_count || (crew.members || []).length} members
                        </span>
                        {crew.region && (
                            <span className="inline-flex items-center gap-1.5">
                                <MapPin size={14} /> {crew.region}
                            </span>
                        )}
                    </div>

                    {user && (
                        <button
                            onClick={toggleJoin}
                            disabled={joining}
                            className={`mt-6 px-5 py-2.5 rounded-sm font-semibold tracking-wide text-sm transition-colors ${
                                isMember
                                    ? "bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800"
                                    : "bg-red-600 hover:bg-red-700 text-white"
                            }`}
                            data-testid="crew-join-btn"
                        >
                            {joining ? "…" : isMember ? "Leave Crew" : "Join Crew"}
                        </button>
                    )}
                </div>

                <aside>
                    <p className="overline mb-3">Members</p>
                    <div className="space-y-2">
                        {(crew.members_full || []).slice(0, 12).map((m) => (
                            <Link
                                to={`/u/${m.username}`}
                                key={m.id}
                                className="flex items-center gap-3 bg-zinc-900/40 border border-zinc-800/50 rounded-sm p-3 hover:border-zinc-700 transition-colors"
                            >
                                <div className="w-9 h-9 rounded-sm bg-zinc-800 flex items-center justify-center font-display font-black text-zinc-300">
                                    {m.username?.[0]?.toUpperCase()}
                                </div>
                                <div>
                                    <p className="font-body text-sm text-zinc-200">{m.name}</p>
                                    <p className="text-xs text-zinc-500 font-body">@{m.username}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </aside>
            </main>
            <Footer />
        </div>
    );
}
